import { useMutation } from '@apollo/client';
import type { UpdateCustomRoleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateCustomRoleDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateCustomRoleTRPCInput } from './useUpdateCustomRoleTRPC';
import { useUpdateCustomRoleTRPC } from './useUpdateCustomRoleTRPC';

/**
 * Flag-gated wrapper. Consumers ALWAYS import this hook (never the
 * `*TRPC` sibling directly). When the `trpc` flag is on, the call is
 * routed to the tRPC mutation; otherwise it falls back to the legacy
 * Apollo `customRoleUpdate` GraphQL action.
 */
export const useUpdateCustomRole = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // Apollo mutation — legacy fallback. Evict both the list field and
  // the by-pk field so the detail-page refetch and the settings list
  // pick up the rename.
  const [updateCustomRoleGraphQL, graphqlState] = useMutation(
    UpdateCustomRoleDocument,
    {
      update: (cache) => {
        evictField(cache, 'custom_role');
        evictField(cache, 'custom_role_by_pk');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateCustomRoleTRPC();

  const updateCustomRole = async (
    variables: UpdateCustomRoleTRPCInput
  ): Promise<UpdateCustomRoleMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateCustomRole(variables);
    }

    const result = await updateCustomRoleGraphQL({
      variables: {
        input: {
          Id: variables.id,
          Name: variables.name,
          Description: variables.description ?? undefined,
          RoleKeys: variables.roleKeys,
          UserIds: variables.userIds,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to update custom role');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateCustomRole,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateCustomRole,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
